import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/SiteLeads_ListaInput';
import '../contracts/SiteLeads_ListaResponse';
import '../contracts/SiteLeads_GetResponse';
import type { SiteLeads_ListaInput } from '../contracts/SiteLeads_ListaInput';
import type { SiteLeads_LeadRow } from '../contracts/SiteLeads_LeadRow';

/**
 * Composable for the "Leads do site" staff panel (institutional website leads,
 * Full-only). Mirrors useSiteUnidades.ts.
 *
 * READ ONLY, and that is the design (plan fase 5): this domain is a diary of
 * what the site received, not a pipeline. The only write anywhere in it is the
 * site's own signed webhook, which no browser ever calls.
 */
export function useSiteLeads() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch one page of the log. Every filter is optional and a blank value is
   * "no filter" — it is left off the query string entirely rather than sent as
   * an empty-string match.
   */
  const fetchLeads = async (filtros: SiteLeads_ListaInput = {}) => {
    log('useSiteLeads', 'fetchLeads', 'Fetching leads', filtros);
    isLoading.value = true;
    error.value = null;

    try {
      const params = new URLSearchParams();
      if (filtros.formulario) params.set('formulario', filtros.formulario);
      if (filtros.de) params.set('de', filtros.de);
      if (filtros.ate) params.set('ate', filtros.ate);
      if (filtros.limit != null) params.set('limit', String(filtros.limit));
      if (filtros.offset != null) params.set('offset', String(filtros.offset));

      const query = params.toString();
      const url = '/index.php/config/site_leads/lista' + (query === '' ? '' : `?${query}`);

      const response = await httpService.get(url, { responseContract: 'SiteLeads_ListaResponse' });
      log('useSiteLeads', 'fetchLeads', 'Response', response);

      if (response.success) {
        return {
          items: (response.dados?.items || []) as SiteLeads_LeadRow[],
          total: response.dados?.total ?? 0,
          limit: response.dados?.limit ?? 0,
          offset: response.dados?.offset ?? 0
        };
      } else {
        throw new Error(response.error || 'Erro ao buscar leads');
      }
    } catch (err) {
      logError('useSiteLeads', 'fetchLeads', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch one log entry by ID — the whole envelope, which the list omits.
   */
  const fetchLead = async (leadId: number) => {
    log('useSiteLeads', 'fetchLead', 'Fetching lead', { leadId, type: typeof leadId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/site_leads/get/${leadId}`, { responseContract: 'SiteLeads_GetResponse' });
      log('useSiteLeads', 'fetchLead', 'Response', response);

      if (response.success) {
        return response.dados?.lead as SiteLeads_LeadRow | undefined;
      } else {
        const errorMsg = response.error || 'Erro ao buscar lead';
        logError('useSiteLeads', 'fetchLead', 'Backend returned error', { response, error: errorMsg });
        throw new Error(errorMsg);
      }
    } catch (err) {
      logError('useSiteLeads', 'fetchLead', 'Error', {
        leadId,
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        data: err.data,
        response: err.response
      });
      error.value = err.message;

      if ((err as any).data) {
        const enhancedError = new Error((err as any).data.error || (err as any).data.Error || err.message);
        (enhancedError as any).originalError = err;
        (enhancedError as any).response = (err as any).response;
        (enhancedError as any).data = (err as any).data;
        throw enhancedError;
      }

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Operations (read only — the panel has no write)
    fetchLeads,
    fetchLead
  };
}
